import React, { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import usePaginatedResource from '../../../hooks/usePaginatedResource';
import useResourceMutation from '../../../hooks/useResourceMutation';
import PageHeader from '../../../components/shared/PageHeader/PageHeader';
import DataTable, { Column } from '../../../components/shared/DataTable/DataTable';
import Modal from '../../../components/shared/Modal/Modal';
import ConfirmDialog from '../../../components/shared/ConfirmDialog/ConfirmDialog';
import Button from '../../../components/shared/Button/Button';
import FormField from '../../../components/shared/FormField/FormField';
import Input from '../../../components/shared/Input/Input';
import { createRoute, deleteRoute, updateRoute, setRouteStops, setRouteFares, getRoute } from '../../../services/routes';
import { listStops } from '../../../services/stops';
import RouteStopsBuilder from '../../../components/admin/RouteStopsBuilder';
import RouteFareMatrix from '../../../components/admin/RouteFareMatrix';
import type { Route, Stop, RouteStop, RouteFare } from '../../../types/api';

interface FormState {
  name: string;
  estimated_duration: string;
  is_active: boolean;
  stops: RouteStop[];
  fares: RouteFare[];
}

const empty: FormState = {
  name: '', estimated_duration: '', is_active: true, stops: [], fares: [],
};

const RoutesPage: React.FC = () => {
  const { data: routes, loading, refetch } = usePaginatedResource<Route>('/routes/');

  const [editing, setEditing] = useState<Route | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<FormState>(empty);
  const [deleteTarget, setDeleteTarget] = useState<Route | null>(null);
  const [allStops, setAllStops] = useState<Stop[]>([]);

  useEffect(() => { listStops(false).then(setAllStops); }, []);

  const save = useResourceMutation(
    async (state: FormState) => {
      const basePayload: Partial<Route> = {
        name: state.name,
        estimated_duration: state.estimated_duration || null,
        is_active: state.is_active,
      };
      const route = editing
        ? await updateRoute(editing.id, basePayload)
        : await createRoute(basePayload);
      if (state.stops.length >= 2) {
        await setRouteStops(route.id, state.stops.map(s => ({
          stop_id: s.stop_id, sequence: s.sequence, minutes_from_origin: s.minutes_from_origin,
        })));
      }
      if (state.fares.length > 0) {
        await setRouteFares(route.id, state.fares.map(f => ({
          boarding_stop_id: f.boarding_stop_id,
          alighting_stop_id: f.alighting_stop_id,
          amount: f.amount,
        })));
      }
      return route;
    },
    { successMessage: editing ? 'Route updated' : 'Route created' }
  );

  const del = useResourceMutation(
    (id: number) => deleteRoute(id),
    { successMessage: 'Route deleted' }
  );

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setShowForm(true);
  };

  const openEdit = async (r: Route) => {
    setEditing(r);
    const full = await getRoute(r.id);
    setForm({
      name: full.name,
      estimated_duration: full.estimated_duration ?? '',
      is_active: full.is_active,
      stops: full.stops ?? [],
      fares: full.fares ?? [],
    });
    setShowForm(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await save.run(form);
      setShowForm(false);
      setForm(empty);
      setEditing(null);
      refetch();
    } catch { /* toast */ }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await del.run(deleteTarget.id);
      setDeleteTarget(null);
      refetch();
    } catch { /* toast */ }
  };

  const columns: Column<Route>[] = [
    { key: 'name', header: 'Route', sortable: true },
    { key: 'origin_name', header: 'From', render: (r) => r.origin_name ?? '—' },
    { key: 'destination_name', header: 'To', render: (r) => r.destination_name ?? '—' },
    { key: 'estimated_duration', header: 'Duration',
      render: (r) => r.estimated_duration ?? '—' },
    { key: 'is_active', header: 'Active', filterable: true, filterOptions: [
      { value: 'true', label: 'Active' },
      { value: 'false', label: 'Inactive' },
    ], render: (r) => (
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${
        r.is_active ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'
      }`}>
        <span className={`h-1.5 w-1.5 rounded-full ${r.is_active ? 'bg-emerald-500' : 'bg-slate-400'}`} aria-hidden />
        {r.is_active ? 'Active' : 'Inactive'}
      </span>
    ) },
  ];

  const actions = (r: Route) => (
    <div className="inline-flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => openEdit(r)}>Edit</Button>
      <Button variant="danger" size="sm" onClick={() => setDeleteTarget(r)}>Delete</Button>
    </div>
  );

  return (
    <>
      <PageHeader
        title="Routes"
        description="Route network — define stops and fares for each route."
        actions={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New route
          </Button>
        }
      />

      <DataTable<Route>
        columns={columns}
        data={routes}
        rowKey="id"
        loading={loading}
        emptyTitle="No routes defined"
        emptyDescription="Add routes between cities to start scheduling trips."
        emptyAction={
          <Button onClick={openCreate} icon={<Plus className="h-4 w-4" />}>
            New route
          </Button>
        }
        actions={actions}
      />

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editing ? 'Edit route' : 'New route'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowForm(false)} disabled={save.loading}>Cancel</Button>
            <Button onClick={handleSave} loading={save.loading}>{editing ? 'Save changes' : 'Create route'}</Button>
          </>
        }
      >
        <form onSubmit={handleSave} className="space-y-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Route name" name="name" required error={save.fieldErrors.name}>
              <Input id="name" required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </FormField>
            <FormField label="Estimated duration" name="estimated_duration" hint="HH:MM:SS">
              <Input id="estimated_duration" value={form.estimated_duration}
                onChange={(e) => setForm({ ...form, estimated_duration: e.target.value })}
                placeholder="HH:MM:SS" />
            </FormField>
            <FormField label="Active" name="is_active">
              <label className="inline-flex items-center gap-2.5 rounded-lg border border-slate-300 bg-white px-3.5 h-11">
                <input type="checkbox" checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-600" />
                <span className="text-sm text-slate-700">Route is currently active</span>
              </label>
            </FormField>
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Stops</h3>
            <RouteStopsBuilder
              allStops={allStops}
              stops={form.stops}
              onChange={(stops) => setForm({ ...form, stops })}
            />
          </div>

          <div>
            <h3 className="mb-2 text-sm font-semibold text-slate-700">Fare matrix (KES)</h3>
            <RouteFareMatrix
              stops={form.stops}
              fares={form.fares}
              onChange={(fares) => setForm({ ...form, fares })}
            />
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete route?"
        message={`Remove route "${deleteTarget?.name ?? ''}"? Existing trips referencing it will be blocked from deletion.`}
        confirmLabel="Delete"
        variant="danger"
        loading={del.loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </>
  );
};

export default RoutesPage;
